import React, { createContext, useContext, useState, useEffect } from 'react';
import { db, testFirestoreConnection } from '../firebaseConfig';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { useAuth } from './AuthContext';

const OrderStatusContext = createContext();

export const useOrderStatus = () => {
  const context = useContext(OrderStatusContext);
  if (!context) {
    throw new Error('useOrderStatus must be used within an OrderStatusProvider');
  }
  return context;
};

// Order status definitions - moved outside component to prevent re-creation
const ORDER_STATUSES = {
    PLACED: {
      id: 'placed',
      message: 'Order placed, preparation will begin shortly',
      icon: '📋',
      color: '#3B82F6'
    },
    ACCEPTED: {
      id: 'accepted',
      message: 'Food preparation started',
      icon: '👨‍🍳',
      color: '#F59E0B'
    },
    PREPARING: {
      id: 'preparing',
      message: 'Food preparation in progress',
      icon: '🔥',
      color: '#F59E0B'
    },
    READY: {
      id: 'ready',
      message: 'Order ready, delivery partner will pickup order soon',
      icon: '✅',
      color: '#10B981'
    },
    OUT_FOR_DELIVERY: {
      id: 'out_for_delivery',
      message: 'Order out for delivery',
      icon: '🛵',
      color: '#8B5CF6'
    },
    DELIVERED: {
      id: 'delivered',
      message: 'Order delivered successfully',
      icon: '🎉',
      color: '#10B981'
    }
  };

export const OrderStatusProvider = ({ children }) => {
  console.log('🔄 OrderStatusProvider: Component rendering');
  console.log('🌐 Environment:', process.env.NODE_ENV);
  console.log('🌐 Host:', window.location.host);
  console.log('🌐 Protocol:', window.location.protocol);
  
  const [currentOrder, setCurrentOrder] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  // Listen for current order status
  useEffect(() => {
    console.log('🔄 OrderStatusContext: useEffect triggered');
    
    const setupOrderListener = async () => {
      // Test Firestore connection first
      const isConnected = await testFirestoreConnection();
      if (!isConnected) {
        console.error('❌ Cannot listen to orders - Firestore connection failed');
        setIsLoading(false);
        return;
      }
      
      // Get phone number from localStorage
      const customerPhone = localStorage.getItem('customerPhone') || localStorage.getItem('userPhone');
      
      console.log('🔍 OrderStatusContext: Debug info', {
        user: user,
        userUid: user?.uid,
        customerPhone: customerPhone,
        localStorageKeys: Object.keys(localStorage),
        localStorageCustomerPhone: localStorage.getItem('customerPhone'),
        localStorageUserPhone: localStorage.getItem('userPhone')
      });
    
      if (!user?.uid && !customerPhone) {
        console.log('🔍 OrderStatusContext: No user UID and no phone number, clearing order status');
        setCurrentOrder(null);
        setOrderStatus(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
    
    // Listen for the most recent order for this user
    const ordersRef = collection(db, 'orders');
    
    console.log('🔍 OrderStatusContext: Setting up listener', {
      userId: user?.uid,
      customerPhone: customerPhone
    });
    
    // If user is authenticated, query by userId first
    if (user?.uid) {
      const q = query(
        ordersRef,
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc'),
        limit(1)
      );
    
      const unsubscribe = onSnapshot(q, (snapshot) => {
          console.log('🔍 OrderStatusContext: User query snapshot received', {
            empty: snapshot.empty,
            size: snapshot.size,
            docs: snapshot.docs.length
          });
          
          if (!snapshot.empty) {
            const orderDoc = snapshot.docs[0];
            const orderData = { id: orderDoc.id, ...orderDoc.data() };
            
            console.log('🔍 OrderStatusContext: Order data found by userId', {
              orderId: orderData.orderId,
              status: orderData.status,
              userId: orderData.userId,
              currentUserId: user.uid
            });
            
            // Only show status if order is not delivered
            if (orderData.status !== 'delivered') {
              console.log('🔍 OrderStatusContext: Setting current order and status', {
                status: orderData.status,
                statusConfig: ORDER_STATUSES[orderData.status?.toUpperCase()] || ORDER_STATUSES.PLACED
              });
              setCurrentOrder(orderData);
              setOrderStatus(ORDER_STATUSES[orderData.status?.toUpperCase()] || ORDER_STATUSES.PLACED);
            } else {
              console.log('🔍 OrderStatusContext: Order is delivered, clearing status');
              setCurrentOrder(null);
              setOrderStatus(null);
            }
          } else {
            console.log('🔍 OrderStatusContext: No orders found by userId, trying phone number fallback');
            
            // If no orders found by userId, try by phone number
            if (customerPhone) {
              console.log('🔍 OrderStatusContext: Trying phone number query', { customerPhone });
              
              const phoneQuery = query(
                ordersRef,
                where('customerPhone', '==', customerPhone),
                orderBy('createdAt', 'desc'),
                limit(1)
              );
              
              const phoneUnsubscribe = onSnapshot(phoneQuery, (phoneSnapshot) => {
                console.log('🔍 OrderStatusContext: Phone query snapshot', {
                  empty: phoneSnapshot.empty,
                  size: phoneSnapshot.size
                });
                
                if (!phoneSnapshot.empty) {
                  const orderDoc = phoneSnapshot.docs[0];
                  const orderData = { id: orderDoc.id, ...orderDoc.data() };
                  
                  console.log('🔍 OrderStatusContext: Phone query found order', {
                    orderId: orderData.orderId,
                    status: orderData.status
                  });
                  
                  if (orderData.status !== 'delivered') {
                    setCurrentOrder(orderData);
                    setOrderStatus(ORDER_STATUSES[orderData.status?.toUpperCase()] || ORDER_STATUSES.PLACED);
                  }
                } else {
                  console.log('🔍 OrderStatusContext: No orders found by phone number either');
                  setCurrentOrder(null);
                  setOrderStatus(null);
                }
              });
              
              // Return cleanup function for phone query
              return () => {
                unsubscribe();
                phoneUnsubscribe();
              };
            } else {
              setCurrentOrder(null);
              setOrderStatus(null);
            }
          }
          setIsLoading(false);
        },
        (error) => {
          console.error('❌ Error listening to order status:', error);
          console.error('❌ Error details:', {
            code: error.code,
            message: error.message,
            stack: error.stack
          });
          setIsLoading(false);
        }
      );

      return () => unsubscribe();
    } else if (customerPhone) {
      // If no user but phone number exists, query by phone number directly
      console.log('🔍 OrderStatusContext: No user, querying by phone number directly', { customerPhone });
      
      const phoneQuery = query(
        ordersRef,
        where('customerPhone', '==', customerPhone),
        orderBy('createdAt', 'desc'),
        limit(1)
      );
      
      const unsubscribe = onSnapshot(phoneQuery, (snapshot) => {
        console.log('🔍 OrderStatusContext: Phone-only query snapshot', {
          empty: snapshot.empty,
          size: snapshot.size,
          docs: snapshot.docs.length
        });
        
        if (!snapshot.empty) {
          const orderDoc = snapshot.docs[0];
          const orderData = { id: orderDoc.id, ...orderDoc.data() };
          
          console.log('🔍 OrderStatusContext: Phone-only query found order', {
            orderId: orderData.orderId,
            status: orderData.status
          });
          
          if (orderData.status !== 'delivered') {
            setCurrentOrder(orderData);
            setOrderStatus(ORDER_STATUSES[orderData.status?.toUpperCase()] || ORDER_STATUSES.PLACED);
          } else {
            setCurrentOrder(null);
            setOrderStatus(null);
          }
        } else {
          console.log('🔍 OrderStatusContext: No orders found by phone number');
          setCurrentOrder(null);
          setOrderStatus(null);
        }
        setIsLoading(false);
      }, (error) => {
        console.error('❌ Error listening to order status by phone:', error);
        console.error('❌ Phone query error details:', {
          code: error.code,
          message: error.message,
          stack: error.stack
        });
        setIsLoading(false);
      });
      
      return () => unsubscribe();
    }
    };
    
    setupOrderListener();
  }, [user?.uid]);

  // Get estimated delivery time
  const getEstimatedDeliveryTime = () => {
    if (!currentOrder) return null;
    
    const orderTime = currentOrder.createdAt?.toDate();
    if (!orderTime) return null;
    
    // Add 30-45 minutes for delivery
    const deliveryTime = new Date(orderTime.getTime() + (35 * 60 * 1000));
    const now = new Date();
    const diffMinutes = Math.ceil((deliveryTime - now) / (1000 * 60));
    
    if (diffMinutes <= 0) return 'arriving now';
    return `arriving in ${diffMinutes} mins`;
  };

  // Clear current order (when user wants to place new order)
  const clearCurrentOrder = () => {
    setCurrentOrder(null);
    setOrderStatus(null);
  };

  const value = {
    currentOrder,
    orderStatus,
    isLoading,
    ORDER_STATUSES,
    getEstimatedDeliveryTime,
    clearCurrentOrder
  };

  return (
    <OrderStatusContext.Provider value={value}>
      {children}
    </OrderStatusContext.Provider>
  );
};
